import { useState } from 'react';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, Pencil, Plus, Tags } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useCategoriesQuery, useUpsertCategoryMutation } from '@/client/api/business/spend';
import type { ExpenseCategory } from '@/client/types/business/spend';
import { BusinessPageShell } from '../components/BusinessPageShell';
import { Field, inputClass } from '../../Accounting/components/spend-ui';
import { DeleteConfirmDialog, EmptyState, RowMenu, StatusBadge } from '../../HumanResource/components/hr-ui';

/** Expense categories: what money goes on. Bills, claims and budgets use them. */
export default function ExpenseTypesPage() {
  const categoriesQ = useCategoriesQuery();
  const upsert = useUpsertCategoryMutation();
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const [editing, setEditing] = useState<ExpenseCategory | null | 'new'>(null);
  const [form, setForm] = useState({ name: '', code: '' });
  const [toggling, setToggling] = useState<ExpenseCategory | null>(null);
  const categories = categoriesQ.data ?? [];

  const open = (c: ExpenseCategory | 'new') => {
    setEditing(c);
    setForm(c === 'new' ? { name: '', code: '' } : { name: c.name, code: c.code });
  };

  return (
    <BusinessPageShell
      mock={false}
      title="Expense categories"
      description="What money goes on. Bills, expense claims and budgets are all filed under these."
      crumbs={[{ label: 'Spend' }, { label: 'Expense categories' }]}
      icon={Tags}
      actions={
        <PermissionButton code={PERMISSIONS.accounting.manage} className="gap-2" onClick={() => open('new')}>
          <Plus className="h-4 w-4" /> Add category
        </PermissionButton>
      }
      stats={[{ label: 'Active categories', value: String(categories.filter((c) => c.status === 'active').length) }]}
    >
      {categoriesQ.isError ? (
        <EmptyState label={`Could not load categories: ${(categoriesQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : categoriesQ.isLoading ? (
        <EmptyState label="Loading categories…" />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Code</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.map((c) => (
                <tr key={c.id} className={canManage ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => canManage && open(c)}>
                  <td className="px-4 py-3 font-medium text-gray-900">{c.name}</td>
                  <td className="px-4 py-3 text-gray-600">{c.code || '—'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge label={c.status} className={`capitalize ${c.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      items={[
                        { label: 'Edit', icon: Pencil, hidden: !canManage, onSelect: () => open(c) },
                        {
                          label: c.status === 'active' ? 'Archive' : 'Restore',
                          icon: c.status === 'active' ? Archive : ArchiveRestore,
                          hidden: !canManage,
                          onSelect: () => setToggling(c),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add category' : 'Edit category'}</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              upsert.mutate(
                { id: editing && editing !== 'new' ? editing.id : undefined, name: form.name, code: form.code },
                {
                  onSuccess: () => {
                    toast.success('Saved');
                    setEditing(null);
                  },
                  onError: (err) => toast.error((err as Error).message || 'Could not save'),
                },
              );
            }}
          >
            <Field label="Name">
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} />
            </Field>
            <Field label="Code (optional)">
              <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className={inputClass} />
            </Field>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={upsert.isPending}>
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!toggling}
        onOpenChange={(o) => !o && setToggling(null)}
        title={toggling?.status === 'active' ? `Archive ${toggling?.name}?` : `Restore ${toggling?.name}?`}
        description={toggling?.status === 'active' ? 'It can no longer be picked. Past bills and claims keep it.' : 'It can be picked again.'}
        actionLabel={toggling?.status === 'active' ? 'Archive' : 'Restore'}
        danger={toggling?.status === 'active'}
        onConfirm={() => {
          if (toggling)
            upsert.mutate(
              { id: toggling.id, status: toggling.status === 'active' ? 'archived' : 'active' },
              { onSuccess: () => toast.success('Saved'), onError: (e) => toast.error((e as Error).message) },
            );
          setToggling(null);
        }}
      />
    </BusinessPageShell>
  );
}
